import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, BarChart3, CheckCircle2, ClipboardCheck, Users, Hash, Lock, BookOpen, Star, Circle } from 'lucide-react';

const STEPS = [
  {
    title: 'Bem-vindo ao Dashboard',
    icon: <BarChart3 className="w-8 h-8 text-indigo-500" />,
    content: (
      <div className="space-y-3">
        <p>O Dashboard Auxiliar é a sua central de operações. Aqui pode:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />Ver um resumo geral com contadores de presentes, organistas, músicos e violeiros.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />Navegar pelas abas: Dashboard, Presença, Pessoas, Aniversariantes, Votações, Reuniões e Relatórios.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Presença — Selecionar Evento',
    icon: <CheckCircle2 className="w-8 h-8 text-green-500" />,
    content: (
      <div className="space-y-3">
        <p>Na aba <strong>Presença</strong>, selecione um evento na lista lateral para começar.</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />Eventos em aberto aparecem com data e tipo.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />Ao selecionar, verá o header com o nome do evento, data e as suas funções atribuídas.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Presença — Marcar Presença/Ausência',
    icon: <Circle className="w-8 h-8 text-red-400" />,
    content: (
      <div className="space-y-3">
        <p>No header do evento, use os botões para confirmar o seu status:</p>
        <div className="flex gap-3 my-3">
          <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 font-medium">
            <Circle className="w-3.5 h-3.5" /> Ausente
          </div>
          <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-green-700 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> Presente
          </div>
        </div>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 flex-shrink-0" /><strong>Ausente:</strong> As suas funções serão automaticamente redirecionadas para outro auxiliar. Aparecerão riscadas no header.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" /><strong>Presente:</strong> Confirma a sua presença e desbloqueia a listagem de pessoas para marcar presenças.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />Ao marcar presente novamente após ausência, as funções <strong>não voltam</strong> — ficam com o substituto.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Presença — Marcar Pessoas',
    icon: <Users className="w-8 h-8 text-blue-500" />,
    content: (
      <div className="space-y-3">
        <p>Após confirmar a sua presença, a listagem de pessoas aparece:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" /><strong>Minhas Pessoas:</strong> Apenas as pessoas das suas filas de recitativo.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" /><strong>Meninos / Meninas:</strong> Todas as pessoas filtradas por género.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" /><strong>Rápida:</strong> Modo de marcação rápida — limpa a busca e foca no campo após cada marcação.</li>
        </ul>
        <p className="text-sm text-slate-500">Use o campo de busca para encontrar uma pessoa pelo nome.</p>
      </div>
    ),
  },
  {
    title: 'Escala do Evento',
    icon: <ClipboardCheck className="w-8 h-8 text-orange-500" />,
    content: (
      <div className="space-y-3">
        <p>O botão de <strong>Escala</strong> (ícone de clipboard) no header do evento abre a escala completa:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />Cada auxiliar aparece numa linha com bolinha de status (verde = presente, vermelha = ausente, cinza = aguardando).</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-pink-400 mt-1.5 flex-shrink-0" />Agrupado por <strong className="text-pink-500">Meninas</strong> e <strong className="text-blue-500">Meninos</strong>.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />O seu género aparece primeiro (expandido); o outro fica colapsado.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />A estrela indica quem é o líder de cada função.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Funções e Insígnia de Líder',
    icon: <Star className="w-8 h-8 text-amber-400" />,
    content: (
      <div className="space-y-3">
        <p>As suas funções aparecem como badges no header do evento:</p>
        <div className="flex flex-wrap gap-2 my-3">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">4a Fila</span>
          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">Apoio Oração</span>
          <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-slate-100 text-slate-400 border border-slate-200 line-through">1a Fila</span>
        </div>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" /><strong>Badge azul:</strong> Função ativa atribuída a si.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" /><strong>Badge cinza riscado:</strong> Função que foi redirecionada para outro auxiliar por ausência.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" /><strong>Estrela dourada:</strong> Indica que é o líder responsável por aquela função neste evento.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Contagem de Recitativos',
    icon: <Hash className="w-8 h-8 text-purple-500" />,
    content: (
      <div className="space-y-3">
        <p>Se a sua função inclui <strong>Contador(a)</strong>, verá o botão <strong>Contagem</strong> após confirmar presença:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />Clique em "Contagem" para abrir o modal e registar quantos recitativos foram realizados.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />O modal é colorido conforme o género (rosa para meninas, azul para meninos).</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />Os dados de contagem são salvos e consultados pelo administrador.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Fechar / Reabrir Evento',
    icon: <Lock className="w-8 h-8 text-orange-600" />,
    content: (
      <div className="space-y-3">
        <p>Após confirmar presença, se o prazo permitir:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" /><strong>Fechar:</strong> Encerra o evento. Antes de fechar, todos os Contadores devem ter submetido os dados de recitativos.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 flex-shrink-0" />Se faltar alguma contagem, o sistema avisa qual Contador e qual função está pendente.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" /><strong>Reabrir:</strong> Após fechar, o botão muda para "Reabrir", permitindo reabrir o evento sem perder dados.</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Histórico',
    icon: <BookOpen className="w-8 h-8 text-slate-500" />,
    content: (
      <div className="space-y-3">
        <p>O botão de <strong>Histórico</strong> (ícone de livro) mostra todas as ações registadas neste evento:</p>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />Quando marcou Presente ou Ausente.</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />Substituições automáticas (ex: "Substituido em 1a Fila (líder) por Arthur").</li>
          <li className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />Desmarcar status (voltar a "Aguardando").</li>
        </ul>
        <p className="text-sm text-slate-500">Todas as ações incluem data e hora para referência.</p>
      </div>
    ),
  },
];

export default function HelpWizardModal({ open, onClose }) {
  const [step, setStep] = useState(0);

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const handleClose = () => {
    setStep(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-100 flex-shrink-0 overflow-hidden">
          <DialogHeader className="p-0 space-y-0">
            <div className="flex items-center gap-3">
              {current.icon}
              <div>
                <DialogTitle className="text-base">{current.title}</DialogTitle>
                <DialogDescription className="text-xs text-slate-400 mt-0.5">Passo {step + 1} de {STEPS.length}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          {/* Progress bar */}
          <div className="flex gap-1 mt-3">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? 'bg-indigo-500' : 'bg-slate-200'}`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-5 text-sm text-slate-700">
          {current.content}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep(s => s - 1)}
            disabled={isFirst}
            className="text-xs"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          <span className="text-[10px] text-slate-400">{step + 1} / {STEPS.length}</span>
          {isLast ? (
            <Button size="sm" onClick={handleClose} className="text-xs bg-indigo-600 hover:bg-indigo-700">
              Concluir
            </Button>
          ) : (
            <Button size="sm" onClick={() => setStep(s => s + 1)} className="text-xs bg-indigo-600 hover:bg-indigo-700">
              Próximo <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
